/**
 * Landing page — public marketing site at `/` (web).
 * Available to everyone, including signed-in app users (official site + product).
 * Native cold start still routes into `/app/` (see useEffect).
 */

import { useEffect, useCallback, useMemo, useState } from 'react'
import { View, Platform } from 'react-native'
import { useSupabaseAuth } from '../src/shared/contexts/SupabaseAuthContext'
import { useTutorial } from '../src/tutorial/hooks/useTutorial'
import {
  resolveFirstRunRoute,
  isReturningRealUser,
} from '../src/onboarding/helpers/resolveFirstRunRoute.helper'
import { useRouter, useLocalSearchParams, type Href } from 'expo-router'
import {
  LandingOrgAdminNewNav,
  LandingHeaderControls,
  LandingHero,
  LandingFeatures,
  LandingPricingPackages,
  LandingGetTheApp,
  LandingCtaBand,
  MarketingPageFooter,
  LandingSectionReveal,
} from '../components/landing'
import {
  LANDING_PAGE_LEGACY_SECTION_CONTACT,
  LANDING_PAGE_LEGACY_SECTION_FEATURES,
  LANDING_SITE_NAV_ROUTES,
} from '../src/landing/constants/landingSiteNav.constants'
import { scrollWebWindowToTop } from '../src/landing/helpers/scrollWebMarketingSection.helper'
import { ErrorBoundary } from '../components/layout'
import { useAnalytics } from '../src/analytics/useAnalytics'
import { ANALYTICS_EVENTS } from '../src/analytics'
import { useApiClient } from '../helpers/apiClient.helper'
import { syncCoinsCheckoutSession } from '../src/economy/helpers/coinsApi'
import { useEconomyStore } from '../src/shared/stores/economyStore'
import { useSnackbar } from '../src/shared/hooks/useSnackbar'
import { CoinStoreModal } from '../src/economy/components/CoinStoreModal'
import { usePublicMarketingWebScrollLayout } from '../src/shared/hooks/usePublicMarketingWebScrollLayout'
import { useAccountAdminDrawerShift } from '../src/accounts/contexts/AccountAdminDrawerShiftContext'
import { getWebAccountAdminDrawerPushStyle } from '../src/accounts/helpers/accountAdminDrawerPushWeb.helper'
import { useTheme } from '../src/theme/useTheme'
import { getLandingNewPageStyles } from '../appStyles/_landingNew.styles'

/**
 * Module-level guard: native cold-start redirect must fire only once per JS
 * session. A per-component useRef would reset whenever the navigator transiently
 * remounts this screen during cross-stack redirects (e.g. /app → /sign-in passes
 * through /), causing an infinite replace loop.
 */
let hasNativeAutoRedirectFired = false

export default function LandingPage() {
  const theme = useTheme()
  const styles = useMemo(() => getLandingNewPageStyles(theme), [theme])
  const { isLoaded, isSignedIn, isAnonymous } = useSupabaseAuth()
  const { markAsSeen } = useTutorial()
  const { shiftPx } = useAccountAdminDrawerShift()
  const router = useRouter()
  const params = useLocalSearchParams<{ section?: string | string[]; coinsPurchased?: string; session_id?: string }>()
  const { track } = useAnalytics()
  const apiClient = useApiClient()
  const { showSnackbar } = useSnackbar()
  const [shopOpen, setShopOpen] = useState(false)
  const [shopAward, setShopAward] = useState(0)

  useEffect(() => {
    track(ANALYTICS_EVENTS.LANDING_VIEWED)
  }, [track])

  // Handle return from Stripe coin purchase (success_url lands here with ?coinsPurchased=true&session_id=cs_...)
  useEffect(() => {
    if (Platform.OS !== 'web' || !isLoaded || !isSignedIn) return
    const { coinsPurchased, session_id: sessionId } = params
    if (coinsPurchased !== 'true' || !sessionId || typeof sessionId !== 'string') return

    const handle = async () => {
      try {
        const { coins, awarded } = await syncCoinsCheckoutSession(
          apiClient as (endpoint: string, options?: Record<string, unknown>) => Promise<{ status: number; data: unknown }>,
          sessionId,
        )
        useEconomyStore.getState().syncCoins(coins)
        setShopAward(awarded)
        setShopOpen(true)
      } catch {
        showSnackbar('Coins purchase recorded. Check your wallet!', 'success', 'Shop')
      } finally {
        // Clean URL params without triggering Expo Router navigation (which remounts and wipes state)
        window.history.replaceState({}, '', '/')
      }
    }
    handle()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn])

  usePublicMarketingWebScrollLayout()

  const onPressNavHome = useCallback(() => {
    if (Platform.OS !== 'web') return
    scrollWebWindowToTop()
  }, [])

  const onPressNavContact = useCallback(() => {
    if (Platform.OS !== 'web') return
    if (typeof document !== 'undefined') {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  const onPressFeatures = useCallback(() => {
    if (Platform.OS !== 'web') return
    if (typeof document !== 'undefined') {
      document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  const onPressPricing = useCallback(() => {
    if (Platform.OS !== 'web') return
    if (typeof document !== 'undefined') {
      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  useEffect(() => {
    if (Platform.OS !== 'web') return
    const raw = params.section
    const s = typeof raw === 'string' ? raw : Array.isArray(raw) ? raw[0] : undefined
    if (s === LANDING_PAGE_LEGACY_SECTION_CONTACT) {
      router.replace(LANDING_SITE_NAV_ROUTES.CONTACT as Href)
      return
    }
    if (s === LANDING_PAGE_LEGACY_SECTION_FEATURES) {
      router.replace(LANDING_SITE_NAV_ROUTES.HOME as Href)
      return
    }
  }, [params.section, router])

  useEffect(() => {
    if (hasNativeAutoRedirectFired || !isLoaded) return
    if (Platform.OS !== 'web') {
      hasNativeAutoRedirectFired = true
      // Guest-first: anyone with a live session (real user or existing guest) resumes in the
      // app; everyone else goes to the Welcome front door (Get Started / I already have an
      // account). The tutorial now sits behind Welcome's Get Started, so it no longer gates entry.
      if (resolveFirstRunRoute({ isSignedIn, isAnonymous }) === 'app') {
        // Registered users may have had their local has_seen_tutorial flag wiped by an OS
        // storage clear / reinstall — self-heal it so other local checks stay consistent.
        if (isReturningRealUser({ isSignedIn, isAnonymous })) void markAsSeen()
        router.replace('/app/')
      } else {
        router.replace('/welcome')
      }
    }
  }, [isLoaded, isSignedIn, isAnonymous, markAsSeen, router])

  // Native waits for auth (the redirect effect above sends signed-in users into /app).
  // Web renders the marketing page regardless: it is identical signed in or out, and
  // waiting on auth emitted an empty page during static export — nothing for a crawler
  // or an ad-network reviewer to read.
  if (Platform.OS !== 'web') return null

  return (
    <ErrorBoundary>
      <CoinStoreModal
        isVisible={shopOpen}
        onDismiss={() => setShopOpen(false)}
        pendingAward={shopAward}
      />
      <View style={styles.scroll}>
        <View
          style={[
            styles.drawerColumn,
            getWebAccountAdminDrawerPushStyle(shiftPx),
          ]}
        >
          <LandingOrgAdminNewNav
            onPressHome={onPressNavHome}
            onPressContact={onPressNavContact}
            onPressFeatures={onPressFeatures}
            onPressPricing={onPressPricing}
            headerRight={<LandingHeaderControls />}
          />

          <View style={styles.pageBody}>
            <LandingSectionReveal delayMs={20}>
              <LandingHero />
            </LandingSectionReveal>
            <LandingSectionReveal delayMs={40}>
              <LandingFeatures />
            </LandingSectionReveal>
            <LandingSectionReveal delayMs={80}>
              <LandingPricingPackages />
            </LandingSectionReveal>
            <LandingSectionReveal delayMs={100}>
              <LandingGetTheApp />
            </LandingSectionReveal>
            <LandingSectionReveal delayMs={120}>
              <LandingCtaBand />
            </LandingSectionReveal>
            <MarketingPageFooter contactMode="scroll" />
          </View>
        </View>
      </View>
    </ErrorBoundary>
  )
}
